import dayjs from "dayjs";

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

/** One row of `countryBlockList.countries`, and the mutation's return shape. */
export interface CountryBlockStatus {
  /** ISO 3166-1 alpha-2, upper case. The id everything here keys on. */
  countryCode: string;
  name: string;
  /**
   * The country's dialling code, E.164 with the `+`.
   *
   * `null` for the 51 countries whose code is shared — `+1` alone covers the US,
   * Canada and 23 Caribbean territories. A number carrying a shared code cannot
   * be attributed to one country, so those countries get no phone blocking at
   * all and the API reports the code as absent rather than guessing.
   */
  phoneCode?: string | null;
  iso3?: string | null;
  numericCode?: string | null;
  continent?: string | null;
  region?: string | null;
  capital?: string | null;
  /**
   * The toggle state. `true` means OTP requests geolocating to this country
   * are refused.
   *
   * The stored config is a *blocklist*, so the field is `blocked`, not
   * `enabled` — an empty list means everything works. The switch on this screen
   * is labelled "Blocked" for exactly that reason: an "Enabled" label would
   * need inverting in two places (`checked={!blocked}`, `blocked: !enabled`)
   * and one of them eventually gets missed.
   */
  blocked: boolean;
  /**
   * Whether this country's **numbers** are being refused right now — `blocked`
   * and a non-null `phoneCode`.
   *
   * Must be shown alongside `blocked`, never inferred from it. Blocking Jamaica
   * sets `blocked` while leaving this `false`, and an admin reading only the
   * switch would believe Jamaican numbers were being turned away when the IP
   * half is all that is running.
   */
  phoneBlocked: boolean;
  /** Why `phoneCode` is null, naming the sharers. `null` when phone blocking works. */
  phoneBlockingNote?: string | null;
  blockedAt?: string | null;
  /** Admin who blocked it. `null` when unblocked, or when set by a hand-edit. */
  blockedBy?: string | null;
  /** `false` for a stored code ISO does not know. Still enforced. */
  recognized: boolean;
}

export interface CountryBlockList {
  countries: Array<CountryBlockStatus>;
  /** Total blocked, ignoring the server-side filter args. */
  blockedCount: number;
  totalCount: number;
  /** Last write to the list — the only audit signal this feature has. */
  updatedAt?: string | null;
  /** Non-fatal problems with the stored row. Show them whenever non-empty. */
  warnings: Array<string>;
}

/** What `setCountryBlocked` returns: the mutable half of a row. */
export type CountryBlockPatch = Pick<
  CountryBlockStatus,
  | "countryCode"
  | "name"
  | "phoneCode"
  | "blocked"
  | "phoneBlocked"
  | "phoneBlockingNote"
  | "blockedAt"
  | "blockedBy"
>;

/** A patch plus when we wrote it, so propagation lag can be told from a revert. */
export interface PendingPatch {
  patch: CountryBlockPatch;
  at: number;
}

/* ------------------------------------------------------------------ *
 * Standing copy
 * ------------------------------------------------------------------ */

/**
 * The API caches the resolved list for 30s per instance. The instance that
 * served the mutation drops its cache immediately, the others catch up within
 * 30s — so never claim a toggle is instant.
 */
export const PROPAGATION_NOTICE = "Applies within a minute.";

export const PROPAGATION_GRACE_MS = 45_000;

/**
 * Copy-pasteable proof of which check refused someone.
 *
 * The IP half of a block is *silent* — a normal verification id and no error —
 * so the user reports "I never got my code" and there is nothing client-side to
 * point at.
 *
 * `blocked_prefix` is in the query for history only: it was written by the
 * removed raw-prefix mechanism and nothing produces it any more, but the old
 * rows are still in the table and dropping it from the query would make a
 * search over past incidents quietly miss them.
 */
export const OTP_SEND_LOG_SQL = `SELECT "createdAt", channel, destination, "ipAddress", outcome
FROM "OtpSendLog"
WHERE outcome IN ('blocked_country', 'blocked_country_phone', 'blocked_prefix')
ORDER BY "createdAt" DESC LIMIT 50;`;

/* ------------------------------------------------------------------ *
 * Filtering — mirrors the server's own `filter` argument
 * ------------------------------------------------------------------ */

export interface CountryFilter {
  /** Case-insensitive substring of name, alpha-2 or alpha-3 code. */
  search: string;
  blockedOnly: boolean;
  /** `""` means every continent. */
  continent: string;
}

export const EMPTY_COUNTRY_FILTER: CountryFilter = {
  search: "",
  blockedOnly: false,
  continent: "",
};

/**
 * Same three fields the server matches on, so switching this screen to the
 * server-side `filter` argument later would not change what an admin sees.
 */
export function matchesSearch(
  country: CountryBlockStatus,
  search: string
): boolean {
  const needle = search.trim().toLowerCase();

  if (!needle) return true;

  return [country.name, country.countryCode, country.iso3].some((field) =>
    (field ?? "").toLowerCase().includes(needle)
  );
}

/**
 * Server order is preserved (countries arrive sorted by name); this only
 * removes rows.
 */
export function filterCountries(
  countries: Array<CountryBlockStatus>,
  filter: CountryFilter
): Array<CountryBlockStatus> {
  return countries.filter((country) => {
    if (filter.blockedOnly && !country.blocked) return false;
    if (filter.continent && country.continent !== filter.continent) return false;

    return matchesSearch(country, filter.search);
  });
}

/** Continents present in the payload, sorted, blanks dropped. */
export function continentsOf(
  countries: Array<CountryBlockStatus>
): Array<string> {
  const seen = new Set<string>();

  countries.forEach((country) => {
    if (country.continent) seen.add(country.continent);
  });

  return Array.from(seen).sort();
}

/* ------------------------------------------------------------------ *
 * Local writes
 * ------------------------------------------------------------------ */

/**
 * Overlays what we last successfully wrote onto what the server just returned.
 *
 * Needed because a refetch can be served by an instance that has not expired
 * its 30-second cache, which would snap a just-toggled switch back and read as
 * a failed save. A patch wins until either the server agrees with it or it is
 * older than the propagation window — past that, a disagreement is a real
 * disagreement (another admin, or a hand-edit) and the server wins.
 *
 * Pure, and evaluated during render: nothing on this screen runs on a timer.
 */
export function applyPatches(
  countries: Array<CountryBlockStatus>,
  patches: Map<string, PendingPatch>,
  now: number = Date.now()
): Array<CountryBlockStatus> {
  if (!patches.size) return countries;

  return countries.map((country) => {
    const pending = patches.get(country.countryCode);

    if (!pending) return country;
    if (pending.patch.blocked === country.blocked) return country;
    if (now - pending.at > PROPAGATION_GRACE_MS) return country;

    return { ...country, ...pending.patch };
  });
}

/**
 * True while a write has been made but the server is still reporting the old
 * value — i.e. the row is showing our value, not the server's.
 */
export function isPending(
  country: CountryBlockStatus,
  patches: Map<string, PendingPatch>,
  now: number = Date.now()
): boolean {
  const pending = patches.get(country.countryCode);

  if (!pending) return false;

  return (
    pending.patch.blocked === country.blocked &&
    now - pending.at <= PROPAGATION_GRACE_MS
  );
}

/* ------------------------------------------------------------------ *
 * Presentation
 * ------------------------------------------------------------------ */

/**
 * Blocked, but only half of it is running: the country's dialling code is
 * shared, so its numbers cannot be matched and only the IP check applies.
 *
 * This is the state the whole `phoneCode` column exists to make visible —
 * someone using one of these numbers from an unblocked IP is not stopped.
 */
export function isIpOnlyBlock(country: CountryBlockStatus): boolean {
  return country.blocked && !country.phoneBlocked;
}

/**
 * What the phone-code cell says when there is no code to show.
 *
 * Reads as a live consequence when the country is blocked and as a plain fact
 * when it is not — "Numbers not blocked" on an unblocked country would be a
 * confusing thing to assert.
 */
export function phoneCoverageLabel(country: CountryBlockStatus): string {
  return isIpOnlyBlock(country) ? "Numbers not blocked" : "Shared code";
}

/** Toast wording for a completed toggle — never claims a block is instant. */
export function summarizeToggle(patch: CountryBlockPatch): string {
  if (!patch.blocked) {
    return `${patch.name} unblocked. ${PROPAGATION_NOTICE}`;
  }

  return patch.phoneBlocked
    ? `${patch.name} blocked — IP addresses and phone numbers. ${PROPAGATION_NOTICE}`
    : `${patch.name} blocked — IP addresses only, its dialling code is shared. ${PROPAGATION_NOTICE}`;
}

export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "";

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format("D MMM YYYY, HH:mm") : "";
}

/**
 * The audit line for one row.
 *
 * `blockedBy` is null both when the country is not blocked and when it was
 * blocked by a hand-edit of the AppConfig row, so an absent name is reported as
 * unknown rather than omitted — "blocked, by nobody" would be misleading.
 */
export function describeBlockedSince(country: CountryBlockStatus): string {
  if (!country.blocked) return "—";

  const when = formatTimestamp(country.blockedAt);
  const who = wasMigrated(country)
    ? "carried over from the old prefix list"
    : country.blockedBy
    ? `by ${country.blockedBy}`
    : "by an unknown editor";

  return when ? `${when} · ${who}` : who;
}

/**
 * Toast wording for a failed toggle.
 *
 * GraphQL errors arrive as HTTP 200 with an `errors` array, so the code is read
 * off `extensions.code` rather than any status. The two documented codes get
 * wording an admin can act on; anything else falls through to the raw message.
 */
export function describeToggleFailure(
  error: unknown,
  country: CountryBlockStatus,
  blocked: boolean
): string {
  const action = blocked ? "block" : "unblock";
  const code = graphQLErrorCode(error);

  if (code === "INVALID_COUNTRY_CODE") {
    return `The server rejected "${country.countryCode}" as a country code, so ${country.name} was not ${action}ed.`;
  }

  if (code === "FORBIDDEN") {
    return "Your account is not allowed to change the country blocklist. Sign in as an admin and try again.";
  }

  const message = (error as Error | undefined)?.message;

  return message
    ? `Could not ${action} ${country.name}: ${message}`
    : `Could not ${action} ${country.name}.`;
}

function graphQLErrorCode(error: unknown): string | undefined {
  const errors = (error as { graphQLErrors?: Array<{ extensions?: { code?: unknown } }> })
    ?.graphQLErrors;

  const code = errors?.[0]?.extensions?.code;

  return typeof code === "string" ? code : undefined;
}

/* ------------------------------------------------------------------ *
 * The list that used to sit alongside this one
 * ------------------------------------------------------------------ */

/**
 * `blockedBy` on a row the merge migration wrote, rather than an admin.
 *
 * The raw-prefix mechanism that used to block countries separately was folded
 * into this list, carrying its 14 live prefixes over as country blocks. So this
 * screen opens showing countries nobody on the team remembers blocking, and
 * `blockedBy` reading `migration:blocked-country-prefixes` is the only
 * explanation available.
 */
export const MIGRATION_ACTOR_PREFIX = "migration:";

export function wasMigrated(country: CountryBlockStatus): boolean {
  return Boolean(country.blockedBy?.startsWith(MIGRATION_ACTOR_PREFIX));
}
